import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  ChevronDown,
  ChevronLeft,
  Compass,
  Plus,
  Settings,
  Trash2,
  AlertOctagon,
} from "lucide-react";

import { useCandidate } from "@/application/use-cases/candidates/use-candidates.queries";
import {
  useCandidateChoices,
  useUpsertCandidateChoice,
} from "@/application/use-cases/candidates/use-candidate-choices.queries";
import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import { useDefaultWeightsPreset } from "@/application/use-cases/products/use-weights.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { useUpsertCandidate } from "@/application/use-cases/candidates/use-candidates.queries";
import {
  computeCandidateAggregate,
  type CandidateAggregateResult,
  type CoverageBreakdown,
} from "@/application/use-cases/candidates/compute-candidate-aggregates";
import {
  deriveCandidateProsCons,
  type DerivedBullet,
} from "@/application/use-cases/candidates/derive-candidate-pros-cons";
import {
  useComponentCriticality,
  useUpsertComponentCriticality,
} from "@/application/use-cases/candidates/use-component-criticality.queries";
import { ActivePresetSelector } from "@/presentation/components/products/active-preset-selector";
import { ComponentPickerRow } from "@/presentation/components/candidates/component-picker-row";
import { EditCandidateMetaDialog } from "@/presentation/components/candidates/edit-candidate-meta-dialog";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  architectureComponents,
  criticalityOf,
  type ArchitectureComponent,
} from "@/presentation/data/architecture-components";
import { DOMAIN_COLOR, DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";

export const Route = createFileRoute("/_app/candidates/$slug")({
  loader: ({ params }) => {
    if (!params.slug) throw notFound();
    return null;
  },
  component: CandidateDetailPage,
});

function CandidateDetailPage() {
  const { slug } = Route.useParams();
  const candidateQ = useCandidate(slug);
  const choicesQ = useCandidateChoices(candidateQ.data?.id);
  const productsQ = useProducts();
  const scoresQ = useAllProductScores();
  const criteriaQ = useEvaluationCriteria();
  const presetQ = useDefaultWeightsPreset();
  const featuresQ = useFeaturesList();
  const fpQ = useAllFeatureProducts();
  const verdictsQ = useAllVerdicts();
  const criticalityQ = useComponentCriticality();
  const upsertChoice = useUpsertCandidateChoice();
  const upsertCandidate = useUpsertCandidate();
  const upsertCriticality = useUpsertComponentCriticality();
  const navigate = useNavigate();

  const [editMetaOpen, setEditMetaOpen] = useState(false);
  const [tradeoffOpen, setTradeoffOpen] = useState(false);

  const candidate = candidateQ.data;
  const choices = choicesQ.data ?? [];
  const products = productsQ.data ?? [];
  const productScores = scoresQ.data ?? [];
  const criteria = criteriaQ.data ?? [];
  const weights = presetQ.data?.weights ?? {};
  const features = featuresQ.data ?? [];
  const featureProducts = fpQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];
  const criticalityOverrides = criticalityQ.data ?? {};

  const choiceByComponent = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const c of choices) m.set(c.component_id, c.product_id);
    return m;
  }, [choices]);

  const aggInputs = useMemo(
    () => ({
      choices,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    }),
    [
      choices,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    ]
  );

  const agg = useMemo(
    () => (candidate ? computeCandidateAggregate(aggInputs) : null),
    [candidate, aggInputs]
  );

  const derived = useMemo(
    () => (candidate && agg ? deriveCandidateProsCons(aggInputs, agg) : null),
    [candidate, agg, aggInputs]
  );

  if (candidateQ.isLoading) {
    return <LoadingState />;
  }
  if (!candidate || !agg) {
    return <EmptyState slug={slug} />;
  }

  const componentsByDomain = groupComponentsByDomain();

  const handlePick = (componentId: string, productId: string | null) => {
    upsertChoice.mutate({
      candidate_id: candidate.id,
      component_id: componentId,
      product_id: productId,
      override_notes_md: null,
    });
  };

  const handleCriticalityChange = (componentId: string, value: number) => {
    upsertCriticality.mutate({ component_id: componentId, criticality: value });
  };

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <StickyCondensedHeader
        slug={slug}
        candidateName={candidate.name}
        composite={agg.composite}
        hasDealbreakers={agg.dealbreakerOrigins.length > 0}
        onOpenSettings={() => setEditMetaOpen(true)}
      />

      <RouteHero
        eyebrow={`Architecture candidate · ${slug}`}
        icon={Compass}
        numeral={agg.composite !== null ? agg.composite.toFixed(2) : "—"}
        numeralTone={
          agg.dealbreakerOrigins.length > 0
            ? "red"
            : agg.composite !== null && agg.composite >= 3.5
              ? "cyan"
              : "amber"
        }
        numeralSuffix="/ 5.00"
        title={candidate.name}
        meta={[
          {
            label: "TTM",
            value: candidate.time_to_mvp_weeks !== null ? `${candidate.time_to_mvp_weeks}w` : "—",
          },
          {
            label: "TCO",
            value: formatTcoRange(candidate.tco_low_usd, candidate.tco_high_usd),
          },
          {
            label: "Dealbreakers",
            value: agg.dealbreakerOrigins.length,
            tone: agg.dealbreakerOrigins.length > 0 ? "red" : "default",
          },
          {
            label: "Complete",
            value: `${agg.componentAggregates.filter((c) => !c.isTBD).length} / ${architectureComponents.length}`,
            tone: agg.tbdCount > 0 ? "amber" : "default",
          },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setEditMetaOpen(true)}
            className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(1_0_0_/_0.18)] bg-[oklch(1_0_0_/_0.04)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.97_0.005_240)] transition-colors hover:bg-[oklch(1_0_0_/_0.08)] print:hidden"
          >
            <Settings className="size-3" />
            Candidate settings
          </button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <ActivePresetSelector />
        <span className="text-muted-foreground text-[10.5px]">
          Switch the lens — this composite and every product score recompute.
        </span>
      </div>

      {agg.dealbreakerOrigins.length > 0 ? (
        <div
          className="flex items-start gap-3 rounded-xl border p-4"
          style={{ borderColor: "oklch(0.78 0.18 25 / 0.40)", background: "oklch(0.97 0.04 25)" }}
        >
          <AlertOctagon
            className="mt-0.5 size-4 shrink-0"
            style={{ color: "oklch(0.55 0.20 25)" }}
          />
          <div className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium" style={{ color: "oklch(0.45 0.18 25)" }}>
              Below the line — dealbreaker origins
            </span>
            <ul className="text-foreground/70 flex flex-col gap-0.5 text-[11.5px]">
              {agg.dealbreakerOrigins.map((o, i) => (
                <li key={`${o.productId}-${o.criterionId}-${i}`}>
                  · <strong>{o.productName}</strong> fails <em>{o.criterionName}</em>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <CoverageStrip agg={agg} />

      {derived
        ? (() => {
            const bet = synthBet(derived);
            return bet ? <BetCard bet={bet} /> : null;
          })()
        : null}

      <CollapsibleTradeoff open={tradeoffOpen} onToggle={() => setTradeoffOpen((v) => !v)}>
        <TradeOffBalanceSheet
          derived={derived}
          customPros={parseBullets(candidate.pros_md)}
          customCons={parseBullets(candidate.cons_md)}
          isPending={upsertCandidate.isPending}
          onCustomChange={(pros, cons) =>
            upsertCandidate.mutate({
              id: candidate.id,
              slug: candidate.slug,
              name: candidate.name,
              narrative_md: candidate.narrative_md,
              commitments_md: candidate.commitments_md,
              pros_md: serializeBullets(pros),
              cons_md: serializeBullets(cons),
              time_to_mvp_weeks: candidate.time_to_mvp_weeks,
              tco_low_usd: candidate.tco_low_usd,
              tco_high_usd: candidate.tco_high_usd,
            })
          }
        />
      </CollapsibleTradeoff>

      <div className="flex flex-col gap-7">
        <div className="flex items-center justify-between">
          <h2 className="eyebrow">Compose by component</h2>
          <span className="text-foreground/60 text-[11px]">
            Pick a product per component — scores aggregate live.
          </span>
        </div>
        {DOMAINS.map((d) => {
          const list = componentsByDomain.get(d);
          if (!list || list.length === 0) return null;
          return (
            <DomainGroup
              key={d}
              domain={d}
              components={list}
              products={products}
              choiceByComponent={choiceByComponent}
              componentScores={new Map(agg.componentAggregates.map((c) => [c.componentId, c]))}
              criticalityOverrides={criticalityOverrides}
              onPick={handlePick}
              onCriticalityChange={handleCriticalityChange}
            />
          );
        })}
      </div>

      <EditCandidateMetaDialog
        candidate={editMetaOpen ? candidate : null}
        onClose={() => setEditMetaOpen(false)}
        onDeleted={() => void navigate({ to: "/candidates" })}
        onDuplicated={(newSlug) =>
          void navigate({ to: "/candidates/$slug", params: { slug: newSlug } })
        }
      />
    </div>
  );
}

function parseBullets(md: string | null): string[] {
  if (!md) return [];
  return md
    .split("\n")
    .map((s) => s.replace(/^[-•·]\s*/, "").trim())
    .filter((s) => s.length > 0);
}

function serializeBullets(bullets: string[]): string | null {
  if (bullets.length === 0) return null;
  return bullets.map((b) => `- ${b}`).join("\n");
}

function CollapsibleTradeoff({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onToggle}
        className="text-foreground/70 hover:text-foreground inline-flex w-fit items-center gap-1.5 text-[11px] font-medium tracking-wider uppercase transition-colors print:hidden"
      >
        <ChevronDown
          className="size-3.5 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
        {open ? "Hide trade-off · pros & cons" : "Show trade-off · pros & cons"}
      </button>
      {open ? children : null}
    </div>
  );
}

function StickyCondensedHeader({
  slug,
  candidateName,
  composite,
  hasDealbreakers,
  onOpenSettings,
}: {
  slug: string;
  candidateName: string;
  composite: number | null;
  hasDealbreakers: boolean;
  onOpenSettings: () => void;
}) {
  const compositeColor = hasDealbreakers
    ? "oklch(0.55 0.20 25)"
    : composite !== null && composite >= 3.5
      ? "oklch(0.55 0.17 190)"
      : "oklch(0.55 0.18 70)";
  return (
    <div className="sticky top-0 z-40 -mx-6 -mt-6 border-b border-[oklch(0.91_0.008_240)] bg-white/90 px-6 py-2.5 backdrop-blur lg:-mx-10 lg:-mt-10 lg:px-10 print:hidden">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/candidates"
            className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 text-[11.5px] transition-colors"
          >
            <ChevronLeft className="size-3" />
            All
          </Link>
          <span className="text-foreground/40">/</span>
          <span className="text-foreground/55 font-data shrink-0 text-[10.5px] tracking-[0.12em] uppercase">
            {slug}
          </span>
          <span className="text-foreground/40">·</span>
          <span className="truncate text-[13px] font-semibold">{candidateName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="font-data text-foreground/55 text-[10.5px] tracking-[0.12em] uppercase">
            Composite
          </span>
          <span
            className="font-data text-[16px] font-semibold tabular-nums"
            style={{ color: compositeColor }}
          >
            {composite !== null ? composite.toFixed(2) : "—"}
          </span>
          <button
            type="button"
            onClick={onOpenSettings}
            className="text-foreground/55 hover:text-foreground inline-flex items-center gap-1 rounded-md border border-[oklch(0.91_0.008_240)] px-2 py-1 text-[11px] transition-colors"
          >
            <Settings className="size-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

const COVERAGE_COLOR = {
  native: "oklch(0.55 0.17 145)",
  plugin: "oklch(0.55 0.17 260)",
  custom: "oklch(0.60 0.17 50)",
  track: "oklch(0.94 0.005 240)",
} as const;

function CoverageStrip({ agg }: { agg: CandidateAggregateResult }) {
  return (
    <div className="glow-card grid grid-cols-3 gap-3 p-4">
      <Coverage label="B2C coverage" value={agg.coverage.b2c} />
      <Coverage label="B2B MVP coverage" value={agg.coverage.b2bMvp} />
      <Coverage label="B2B non-MVP coverage" value={agg.coverage.b2bNonMvp} />
    </div>
  );
}

function Coverage({ label, value }: { label: string; value: CoverageBreakdown | null }) {
  const uncovered = value ? value.totalInScope - value.totalCovered : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-data text-foreground/55 text-[9.5px] tracking-[0.14em] uppercase">
        {label}
      </span>
      <div className="flex items-baseline gap-2">
        <span
          className="font-data text-[20px] font-semibold tabular-nums"
          style={{ color: value !== null ? COVERAGE_COLOR.native : undefined }}
          title="Share of in-scope features covered NATIVELY (out-of-the-box). The secondary number to the right is total coverage including plugin + custom build work."
        >
          {value !== null ? `${Math.round(value.nativeRatio * 100)}%` : "—"}
          {value !== null ? (
            <span className="text-foreground/45 ml-1 text-[11px] font-medium">native</span>
          ) : null}
        </span>
        {value !== null ? (
          <span className="font-data text-foreground/55 text-[11px] tabular-nums">
            / {Math.round(value.totalRatio * 100)}% total
          </span>
        ) : null}
      </div>
      <div
        className="relative flex h-2 overflow-hidden rounded-full border"
        style={{ background: COVERAGE_COLOR.track, borderColor: "oklch(0.88 0.008 240)" }}
      >
        {value !== null ? (
          <>
            <div
              style={{
                width: `${value.nativeRatio * 100}%`,
                background: COVERAGE_COLOR.native,
              }}
            />
            <div
              style={{
                width: `${value.pluginRatio * 100}%`,
                background: COVERAGE_COLOR.plugin,
              }}
            />
            <div
              style={{
                width: `${value.customRatio * 100}%`,
                background: COVERAGE_COLOR.custom,
              }}
            />
          </>
        ) : null}
      </div>
      {value !== null ? (
        <div className="font-data text-foreground/55 flex flex-wrap items-center gap-x-2 text-[10px] tabular-nums">
          <CoverageLegendItem
            swatch={COVERAGE_COLOR.native}
            label="native"
            count={value.nativeCount}
          />
          <span className="text-foreground/30">·</span>
          <CoverageLegendItem
            swatch={COVERAGE_COLOR.plugin}
            label="plugin"
            count={value.pluginCount}
          />
          <span className="text-foreground/30">·</span>
          <CoverageLegendItem
            swatch={COVERAGE_COLOR.custom}
            label="custom"
            count={value.customCount}
          />
          <span className="text-foreground/30">·</span>
          <CoverageLegendItem swatch={COVERAGE_COLOR.track} label="uncovered" count={uncovered} />
        </div>
      ) : null}
    </div>
  );
}

function CoverageLegendItem({
  swatch,
  label,
  count,
}: {
  swatch: string;
  label: string;
  count: number;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block size-1.5 rounded-sm" style={{ background: swatch }} />
      <span>
        {count} {label}
      </span>
    </span>
  );
}

// ── U2 trade-off balance sheet ─────────────────────────────────────────────
const PRO_ACCENT = "oklch(0.52 0.16 195)";
const CON_ACCENT = "oklch(0.58 0.18 25)";

const MIX_ACCENT = "oklch(0.6 0.13 75)";

interface BetSentence {
  pro: string | null;
  con: string | null;
  /** Set only when the TOP strength is itself split (strong AND weak). */
  mixed: string | null;
}

/** Salience-ordered, lowercased, de-duplicated criterion labels for one side. */
function uniqueCriterionLabels(bullets: DerivedBullet[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of bullets) {
    const label = b.criterionLabel.toLowerCase();
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}

function synthBet(derived: { pros: DerivedBullet[]; cons: DerivedBullet[] }): BetSentence | null {
  const proLabels = uniqueCriterionLabels(derived.pros);
  const conLabels = uniqueCriterionLabels(derived.cons);
  const conSet = new Set(conLabels);

  // If the TOP strength is itself split (also a weakness), don't demote to the
  // #2 strength and call it "strongest" — that's misleading. Surface it as
  // "mixed on X" instead. When the top strength is clean, show it normally.
  const topPro = proLabels[0] ?? null;
  const topProIsSplit = topPro !== null && conSet.has(topPro);
  const mixed = topProIsSplit ? topPro : null;
  const pro = topProIsSplit ? null : topPro;
  const con = conLabels.find((l) => l !== mixed) ?? null;

  if (!pro && !con && !mixed) return null;
  return { pro, con, mixed };
}

function BetCard({ bet }: { bet: BetSentence }) {
  const clauses: ReactNode[] = [];
  if (bet.pro) {
    clauses.push(
      <>
        Strongest on <strong style={{ color: PRO_ACCENT }}>{bet.pro}</strong>
      </>
    );
  }
  if (bet.mixed) {
    clauses.push(
      <>
        Mixed on <strong style={{ color: MIX_ACCENT }}>{bet.mixed}</strong>
      </>
    );
  }
  if (bet.con) {
    clauses.push(
      <>
        weakest on <strong style={{ color: CON_ACCENT }}>{bet.con}</strong>
      </>
    );
  }
  return (
    <div
      className="flex flex-col gap-1 rounded-xl border-l-[3px] px-5 py-4"
      style={{ borderColor: PRO_ACCENT, background: "oklch(0.985 0.012 200)" }}
    >
      <span
        className="font-data text-[10.5px] tracking-[0.16em] uppercase"
        style={{ color: PRO_ACCENT }}
      >
        The bet
      </span>
      <p className="text-[16px] leading-relaxed">
        {clauses.map((c, i) => (
          <span key={i}>
            {i > 0 ? " · " : null}
            {c}
          </span>
        ))}
        .
      </p>
    </div>
  );
}

function TradeOffBalanceSheet({
  derived,
  customPros,
  customCons,
  isPending,
  onCustomChange,
}: {
  derived: { pros: DerivedBullet[]; cons: DerivedBullet[] } | null;
  customPros: string[];
  customCons: string[];
  isPending: boolean;
  onCustomChange: (pros: string[], cons: string[]) => void;
}) {
  const derivedPros = derived?.pros ?? [];
  const derivedCons = derived?.cons ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Trade-off · pros &amp; cons</h2>
        <span className="text-foreground/60 text-[11px]">
          Auto-derived from product scores · add your own with the input at the bottom
        </span>
      </div>

      <div className="glow-card grid grid-cols-1 overflow-hidden p-0 lg:grid-cols-2">
        <BalanceColumn
          tone="pro"
          bullets={derivedPros}
          customBullets={customPros}
          isPending={isPending}
          onAddCustom={(text) => onCustomChange([...customPros, text], customCons)}
          onDeleteCustom={(idx) =>
            onCustomChange(
              customPros.filter((_, i) => i !== idx),
              customCons
            )
          }
        />
        <BalanceColumn
          tone="con"
          bullets={derivedCons}
          customBullets={customCons}
          isPending={isPending}
          onAddCustom={(text) => onCustomChange(customPros, [...customCons, text])}
          onDeleteCustom={(idx) =>
            onCustomChange(
              customPros,
              customCons.filter((_, i) => i !== idx)
            )
          }
        />
      </div>
    </div>
  );
}

function BalanceColumn({
  tone,
  bullets,
  customBullets,
  isPending,
  onAddCustom,
  onDeleteCustom,
}: {
  tone: "pro" | "con";
  bullets: DerivedBullet[];
  customBullets: string[];
  isPending: boolean;
  onAddCustom: (text: string) => void;
  onDeleteCustom: (idx: number) => void;
}) {
  const accent = tone === "pro" ? PRO_ACCENT : CON_ACCENT;
  const heading = tone === "pro" ? "Why yes" : "Why pause";
  const total = bullets.length + customBullets.length;
  const [draft, setDraft] = useState("");
  const submit = () => {
    const v = draft.trim();
    if (v.length === 0) return;
    onAddCustom(v);
    setDraft("");
  };
  return (
    <div
      className="flex flex-col gap-3 p-5"
      style={{
        background: tone === "pro" ? "oklch(0.99 0.01 195)" : "oklch(0.995 0.008 25)",
      }}
    >
      <h3 className="font-data text-[11px] tracking-[0.1em] uppercase" style={{ color: accent }}>
        {heading} · {total}
      </h3>
      {total === 0 ? (
        <p className="text-muted-foreground text-[12px] italic">
          Nothing notable yet. Add your own with the input below.
        </p>
      ) : (
        <div className="flex flex-col">
          {bullets.map((b, i) => (
            <BalanceItem key={b.id} bullet={b} accent={accent} first={i === 0} />
          ))}
          {customBullets.map((text, i) => (
            <CustomBalanceItem
              key={`custom-${i}`}
              text={text}
              accent={accent}
              first={bullets.length === 0 && i === 0}
              isPending={isPending}
              onDelete={() => onDeleteCustom(i)}
            />
          ))}
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="border-foreground/10 mt-1 flex items-center gap-2 border-t pt-2 print:hidden"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={tone === "pro" ? "Add a custom pro…" : "Add a custom con…"}
          disabled={isPending}
          className="text-foreground/85 placeholder:text-foreground/40 flex-1 border-none bg-transparent text-[12.5px] outline-none"
        />
        <button
          type="submit"
          disabled={isPending || draft.trim().length === 0}
          className="text-foreground/55 hover:text-foreground inline-flex shrink-0 items-center gap-1 rounded-md border border-[oklch(0.91_0.008_240)] px-2 py-1 text-[11px] transition-colors disabled:opacity-40"
        >
          <Plus className="size-3" />
          Add
        </button>
      </form>
    </div>
  );
}

function CustomBalanceItem({
  text,
  accent,
  first,
  isPending,
  onDelete,
}: {
  text: string;
  accent: string;
  first: boolean;
  isPending: boolean;
  onDelete: () => void;
}) {
  return (
    <div
      className={`group flex items-start gap-2.5 print:break-inside-avoid ${first ? "" : "border-foreground/10 mt-3 border-t pt-3"}`}
    >
      <span
        className="mt-1.5 inline-block size-1.5 shrink-0 rounded-full"
        style={{ background: accent }}
      />
      <div className="flex-1 text-[13px] leading-relaxed whitespace-pre-wrap">{text}</div>
      <button
        type="button"
        onClick={onDelete}
        disabled={isPending}
        className="text-foreground/40 hover:text-destructive mt-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 print:hidden"
        aria-label="Delete custom bullet"
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  );
}

function BalanceItem({
  bullet,
  accent,
  first,
}: {
  bullet: DerivedBullet;
  accent: string;
  first: boolean;
}) {
  const headline = bullet.strategicImpact?.headline ?? bullet.criterionLabel;
  const soWhat = bullet.strategicImpact?.soWhat ?? null;
  return (
    <div
      className={`flex items-start gap-2.5 print:break-inside-avoid ${first ? "" : "border-foreground/10 mt-3 border-t pt-3"}`}
    >
      <span
        className="mt-1.5 inline-block size-1.5 shrink-0 rounded-full"
        style={{ background: accent }}
      />
      <div className="flex flex-col gap-1">
        <span className="text-[13px] leading-snug font-semibold">{headline}</span>
        {soWhat ? (
          <span className="text-foreground/65 text-[12px] leading-relaxed">{soWhat}</span>
        ) : null}
        <div className="text-foreground/45 font-data mt-0.5 flex flex-wrap items-center gap-2 text-[10px]">
          <span className="rounded bg-[oklch(0.95_0.005_240)] px-1.5 py-0.5">
            {bullet.criterionLabel}
          </span>
          <span>{bullet.scoreLabel}</span>
          <span>weight {bullet.weightPct}%</span>
          {bullet.isDealbreakerEligible ? (
            <span
              className="rounded px-1.5 py-0.5 font-semibold"
              style={{ background: "oklch(0.96 0.035 25)", color: "oklch(0.5 0.17 25)" }}
            >
              dealbreaker-eligible
            </span>
          ) : null}
        </div>
        <details className="mt-0.5">
          <summary className="text-foreground/45 hover:text-foreground/70 font-data cursor-pointer list-none text-[10px] tracking-wider uppercase">
            Evidence
          </summary>
          <span
            className="text-foreground/55 mt-1 block text-[11px] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBulletText(bullet.text) }}
          />
        </details>
      </div>
    </div>
  );
}

function renderBulletText(md: string): string {
  // Minimal markdown: **bold** + newline → <br> (merged bullets join product
  // evidence with \n). Escape everything else.
  const escaped = md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br />");
}

function DomainGroup({
  domain,
  components,
  products,
  choiceByComponent,
  componentScores,
  criticalityOverrides,
  onPick,
  onCriticalityChange,
}: {
  domain: Domain;
  components: ArchitectureComponent[];
  products: ReturnType<typeof useProducts>["data"] extends infer T
    ? T extends (infer U)[]
      ? U[]
      : never
    : never;
  choiceByComponent: Map<string, string | null>;
  componentScores: Map<string, CandidateAggregateResult["componentAggregates"][number]>;
  criticalityOverrides: Record<string, number>;
  onPick: (componentId: string, productId: string | null) => void;
  onCriticalityChange: (componentId: string, value: number) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-2 text-[13px] font-semibold">
        <span
          className="inline-block size-1.5 rounded-full"
          style={{ background: DOMAIN_COLOR[domain] }}
        />
        {DOMAIN_LABEL[domain]}
      </h3>
      <div className="flex flex-col gap-2">
        {components.map((c) => {
          const chosenProductId = choiceByComponent.get(c.id) ?? null;
          const cs = componentScores.get(c.id);
          return (
            <ComponentPickerRow
              key={c.id}
              component={c}
              products={products}
              chosenProductId={chosenProductId}
              score={cs?.score ?? null}
              isDealbreaker={cs?.isDealbreakerFail ?? false}
              criticality={criticalityOf(c, criticalityOverrides)}
              onPick={(productId) => onPick(c.id, productId)}
              onCriticalityChange={(value) => onCriticalityChange(c.id, value)}
            />
          );
        })}
      </div>
    </section>
  );
}

function formatTcoRange(low: number | null, high: number | null): string {
  const k = (n: number) => `₪${Math.round(n / 1000)}k`;
  if (low !== null && high !== null) return `${k(low)}–${k(high).replace("₪", "")}`;
  if (low !== null) return `from ${k(low)}`;
  if (high !== null) return `up to ${k(high)}`;
  return "—";
}

function groupComponentsByDomain() {
  const map = new Map<Domain, ArchitectureComponent[]>();
  for (const c of architectureComponents) {
    const arr = map.get(c.domain) ?? [];
    arr.push(c);
    map.set(c.domain, arr);
  }
  return map;
}

function LoadingState() {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:p-10">
      <div className="bg-muted/40 h-48 animate-pulse rounded-2xl" />
      <div className="bg-muted/30 h-64 animate-pulse rounded-2xl" />
    </div>
  );
}

function EmptyState({ slug }: { slug: string }) {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-3xl flex-col gap-4 p-10 text-center">
      <h1 className="text-xl font-semibold">Candidate not found</h1>
      <p className="text-muted-foreground text-sm">
        No candidate with slug <code className="font-data">{slug}</code>.
      </p>
      <Link to="/candidates" className="text-primary hover:underline">
        ← Back to candidates
      </Link>
    </div>
  );
}
